/**
 * Schemi Zod dei payload di registrazione consensi (modello V2).
 *
 * ADDITIVI: `src/shared/schemas/onboarding.ts` resta intatto in questa
 * fase. La sostituzione dei `literal(true)` del wizard attuale appartiene
 * alle fasi 3 e 4.
 *
 * DUE INVARIANTI DI PROGETTO, entrambe visibili qui:
 *
 * 1. `accepted` e' `z.boolean()`, mai `z.literal(true)`. Il rifiuto deve
 *    essere rappresentabile e registrabile: e' la prova che il consenso
 *    era libero. Cio' che blocca e' l'AVANZAMENTO dell'onboarding, non la
 *    persistenza del record.
 *
 * 2. Il client NON invia l'hash del documento. Manda `documentoId` e
 *    `versione`; la Cloud Function risolve hash e versione attiva dal
 *    registro e rifiuta i payload che referenziano versioni non attive.
 *    Un hash fornito dal chiamante non proverebbe nulla.
 */

import { z } from 'zod';

const trimmedString = (max: number) => z.string().trim().max(max);

/** UUID v4, stessa forma del token di onboarding. */
const tokenSchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    'Link non valido'
  );

export const voceConsensoSchema = z.object({
  /** Id verbatim dal frontmatter del documento (invariante I6). */
  consensoId: trimmedString(128).min(1, 'Identificativo consenso mancante'),
  accepted: z.boolean(),
});
export type VoceConsensoInput = z.infer<typeof voceConsensoSchema>;

export const documentoAccettatoSchema = z.object({
  documentoId: trimmedString(128).min(1, 'Documento mancante'),
  versione: trimmedString(32).min(1, 'Versione mancante'),
  voci: z.array(voceConsensoSchema).min(1, 'Nessuna casella inviata'),
});
export type DocumentoAccettatoInput = z.infer<typeof documentoAccettatoSchema>;

/**
 * Portale paziente: nessuna autenticazione, il token e' la credenziale.
 * L'identita' dell'attore la ricostruisce il server (tokenId, IP, UA).
 */
export const registraConsensiPortaleSchema = z.object({
  token: tokenSchema,
  documenti: z.array(documentoAccettatoSchema).min(1, 'Nessun documento inviato'),
});
export type RegistraConsensiPortaleInput = z.infer<
  typeof registraConsensiPortaleSchema
>;

/**
 * Riacquisizione allo sportello: l'operatore registra cio' che il
 * paziente ha dichiarato di persona. L'attore e' il chiamante
 * autenticato, risolto server-side dal token: non viaggia nel payload.
 */
export const registraConsensiStaffSchema = z.object({
  pazienteId: trimmedString(128).min(1, 'Paziente mancante'),
  documenti: z.array(documentoAccettatoSchema).min(1, 'Nessun documento inviato'),
  /** Annotazione organizzativa, non clinica. Mai contenuti sanitari. */
  nota: trimmedString(500).optional(),
});
export type RegistraConsensiStaffInput = z.infer<
  typeof registraConsensiStaffSchema
>;
